// Singleton — manages browser push notifications for reminders.
// Uses window.Notification (no service worker needed — works while the app is open).
// setTimeout max is ~24.8 days (2^31 ms). For recurring reminders beyond that window,
// the next occurrence is recomputed on reschedule after each firing.

const MAX_DELAY_MS = 2_000_000_000; // ~23 days, safely below 2^31
const _timers = {};

export function requestPermission() {
    if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
    }
}

/**
 * Compute the next occurrence of a reminder after now.
 * For non-recurring reminders, returns the base date if it's in the future.
 * For recurring reminders, finds the nearest future occurrence that also
 * falls on or after the base date (recurrence doesn't start before creation).
 */
function getNextOccurrence(reminder) {
    const dateStr = (reminder.date || '').split('T')[0];
    const timeStr = (reminder.time || '').trim();
    if (!dateStr || !timeStr) return null;

    const [hh, mm] = timeStr.split(':').map(Number);
    if (isNaN(hh) || isNaN(mm)) return null;

    const base = new Date(`${dateStr}T${timeStr}:00`);
    if (isNaN(base.getTime())) return null;

    const now = new Date();
    const recurrence = reminder.recurrence || 'none';

    if (recurrence === 'none') {
        return base > now ? base : null;
    }

    // Build a candidate date: start from today at the reminder's time
    const c = new Date();
    c.setHours(hh, mm, 0, 0);

    switch (recurrence) {
        case 'daily': {
            // If today's slot has already passed, move to tomorrow
            if (c <= now) {
                c.setDate(c.getDate() + 1);
                c.setHours(hh, mm, 0, 0);
            }
            break;
        }
        case 'weekly': {
            const targetDow = base.getDay();
            const curDow = c.getDay();
            let diff = (targetDow - curDow + 7) % 7;
            // If it's the right day but time already passed (or exactly now), push a week
            if (diff === 0 && c <= now) diff = 7;
            c.setDate(c.getDate() + diff);
            c.setHours(hh, mm, 0, 0);
            break;
        }
        case 'monthly': {
            const targetDay = base.getDate();
            c.setDate(targetDay);
            // If the date rolled back (e.g., month has fewer days) or time passed
            if (c.getDate() !== targetDay || c <= now) {
                c.setMonth(c.getMonth() + 1);
                c.setDate(targetDay);
            }
            break;
        }
        case 'yearly': {
            c.setMonth(base.getMonth(), base.getDate());
            if (c <= now) {
                c.setFullYear(c.getFullYear() + 1);
                c.setMonth(base.getMonth(), base.getDate());
            }
            break;
        }
        default:
            return base > now ? base : null;
    }

    // Recurrence can't start before the base date
    if (c < base) return null;

    return c > now ? c : null;
}

export function scheduleReminder(reminder) {
    if (!reminder._id || !reminder.date || !reminder.time) return;

    cancelReminder(reminder._id);

    const next = getNextOccurrence(reminder);
    if (!next) return;

    const delay = next.getTime() - Date.now();
    if (delay <= 0 || delay > MAX_DELAY_MS) return;

    _timers[reminder._id] = setTimeout(() => {
        if ('Notification' in window && Notification.permission === 'granted') {
            const timeStr = (reminder.time || '').trim();
            new Notification(`🔔 ${reminder.title}`, {
                body: reminder.description || `Rappel prévu à ${timeStr}`,
                icon: '/favicon.ico',
            });
        }
        delete _timers[reminder._id];

        // For recurring reminders, reschedule for the next occurrence
        if (reminder.recurrence && reminder.recurrence !== 'none') {
            scheduleReminder(reminder);
        }
    }, delay);
}

export function scheduleAllReminders(reminders) {
    // Cancel all pending timers
    Object.values(_timers).forEach(id => clearTimeout(id));
    Object.keys(_timers).forEach(k => delete _timers[k]);
    (reminders || []).forEach(scheduleReminder);
}

export function cancelReminder(id) {
    if (_timers[id]) {
        clearTimeout(_timers[id]);
        delete _timers[id];
    }
}

export default { requestPermission, scheduleReminder, scheduleAllReminders, cancelReminder };
